import { H5 } from '@wheely/ui-kit';
import cn from 'classnames';
import React from 'react';

import { needsNewFile } from '@/shared/store/doc-uploader/upload-form';

import { DocUploadBlock, TOnChangeValue } from '../DocUploadBlock';
import { ExpiresAtField } from '../ExpiresAtField';

import s from './styles.module.css';
import { ISupplementaryDoc } from './supplementary-docs';

interface IProps<TDocType extends string> {
  doc: ISupplementaryDoc<TDocType>;
  onChangeValue: TOnChangeValue<TDocType>;
  isUploading: boolean;
  error?: string;
  // "main" lays the document out like the main one; "nested" frames it as part of an answer
  variant?: 'main' | 'nested';
}

const SupplementaryDocUpload = <TDocType extends string>({
  doc: { definition, form, question },
  onChangeValue,
  isUploading,
  error,
  variant = 'nested',
}: IProps<TDocType>) => {
  const extensions = question.fileFormat?.extensions;
  const testIdPrefix = `dialog__${form.docType}`;

  return (
    <DocUploadBlock
      variant={variant}
      className={cn({
        [s.expandedDoc]: variant === 'nested',
        [s.expandedDocRejected]: variant === 'nested' && needsNewFile(form),
      })}
      title={
        variant === 'main' ? (
          <H5 capitalize={true} data-test-id={`${testIdPrefix}__title`}>
            {definition.title.trim()}
          </H5>
        ) : (
          <span className={s.expandedDocTitle}>{definition.title.trim()}</span>
        )
      }
      description={definition.desc}
      form={form}
      onChangeValue={onChangeValue}
      isUploading={isUploading}
      hideStatus={question.silentReview}
      accept={extensions?.join(',')}
      fileTypes={extensions}
      error={error}
      testIdPrefix={testIdPrefix}
      aside={
        definition.isExpires && (
          <ExpiresAtField
            form={form}
            onChangeValue={onChangeValue}
            isExpires={true}
            isUploading={isUploading}
            testId={`${testIdPrefix}__expires-at`}
          />
        )
      }
    />
  );
};

export { SupplementaryDocUpload };
